import { useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { api, type AgentStats, type ConnectorSummary, type SafeUser } from "../lib/api";
import { useWS } from "../lib/ws";
import {
  greeting,
  formatRelativeTime,
  resolveAgentStatus,
  systemSummary,
} from "../lib/dashboardUtils";
import { PipelineStage, type StageTone } from "../components/dashboard/PipelineStage";
import { ConnectorsList } from "../components/dashboard/ConnectorsList";
import { SetupChecklist } from "../components/dashboard/SetupChecklist";
import { AgentPanel } from "../components/dashboard/AgentPanel";
import { QuickLinks } from "../components/dashboard/QuickLinks";

interface Source {
  id: string;
  name: string;
  connectorId: string;
  enabled: boolean;
  lastStatus?: string;
}

export function DashboardPage() {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<AgentStats>("/api/v1/stats"),
    refetchInterval: 15_000,
  });

  // Invalidate stats on crawl events; throttled to avoid rapid re-renders
  const lastInvalidateRef = useRef(0);
  useWS({
    onEvent: (ev) => {
      if (ev.kind !== "crawl.status" && ev.kind !== "crawl.entry") return;
      const now = Date.now();
      if (now - lastInvalidateRef.current < 1_000) return;
      lastInvalidateRef.current = now;
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<SafeUser>("/api/v1/me"),
    staleTime: Infinity,
  });

  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get<ConnectorSummary[]>("/api/v1/connectors"),
  });

  const sources = useQuery({
    queryKey: ["sources"],
    queryFn: () => api.get<Source[]>("/api/v1/sources"),
  });

  const data = stats.data;

  const connectorById = new Map<string, ConnectorSummary>(
    (connectors.data ?? []).map((c) => [c.id, c]),
  );

  const sourceCountByConnector = new Map<string, number>();
  for (const s of sources.data ?? []) {
    sourceCountByConnector.set(s.connectorId, (sourceCountByConnector.get(s.connectorId) ?? 0) + 1);
  }

  const totalFailed = data?.runs.last24hFailed ?? 0;
  const totalSucceeded = data?.runs.last24hSucceeded ?? 0;
  const totalRuns = totalSucceeded + totalFailed;
  const isRunning = (data?.runs.last24hRunning ?? 0) > 0;
  const isNewSetup = data != null && data.sources.total === 0;
  const lastCrawlStr = formatRelativeTime(data?.runs.lastFinishedAt);
  const runTone: StageTone = totalFailed > 0 ? "amber" : totalSucceeded > 0 ? "emerald" : "neutral";
  const agentStatus = resolveAgentStatus(data, isRunning);

  return (
    <div className="page-container">
      {/* == HEADER == */}
      <header className="relative pb-6 pt-8">
        <div className="absolute inset-x-0 top-0 -z-10 h-[200px] grid-hero" />
        <div className="eyebrow mb-2">Litmus Catalog Agent</div>
        <h1 className="mb-3 font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
          {greeting()}, <span className="text-brand-700">{me.data?.username ?? "there"}</span>.
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {data?.plantId ? (
            <span className="chip-brand">{data.plantId}</span>
          ) : (
            <button
              onClick={() => setLocation("/settings")}
              className="chip-amber cursor-pointer transition-colors hover:bg-amber-100"
              title="Plant ID is required before any artifact can be built."
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 animate-pulseSoft" />
              Plant ID not configured
            </button>
          )}

          {agentStatus.tone === "emerald" && (
            <span className="chip-emerald">
              {isRunning && (
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 animate-pulseSoft" />
              )}
              {agentStatus.label}
            </span>
          )}
          {agentStatus.tone === "amber" && <span className="chip-amber">{agentStatus.label}</span>}
          {agentStatus.tone === "neutral" && agentStatus.label !== "" && (
            <span className="chip-neutral">{agentStatus.label}</span>
          )}

          {data?.agentVersion && (
            <span className="chip-neutral font-mono">v{data.agentVersion.slice(0, 12)}</span>
          )}
        </div>

        {data && (
          <p className="mt-2.5 text-[13px] text-ash">
            {systemSummary(data, isRunning, lastCrawlStr)}
          </p>
        )}
      </header>

      {/* == PIPELINE OVERVIEW == */}
      <section className="card overflow-hidden">
        <div className="grid grid-cols-2 divide-y divide-rule sm:grid-cols-4 sm:divide-x sm:divide-y-0">
          <PipelineStage
            label="Sources"
            value={data?.sources.total ?? 0}
            sub={
              data && data.sources.total > 0
                ? `${data.sources.enabled} enabled · ${data.sources.total - data.sources.enabled} paused`
                : "None configured"
            }
            tone="neutral"
            onClick={() => setLocation("/sources")}
            icon={
              <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
                <path
                  d="M4 6h12M4 10h8M4 14h10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            }
          />
          <PipelineStage
            label="Ingestion (24h)"
            value={totalRuns}
            sub={
              totalRuns > 0
                ? totalFailed > 0
                  ? `${totalFailed} failed · ${totalSucceeded} succeeded`
                  : "All runs succeeded"
                : "No runs yet"
            }
            tone={runTone}
            icon={
              <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
                <path
                  d="M10 4v6l4 2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            }
          />
          <PipelineStage
            label="Artifacts"
            value={data?.artifacts.total ?? 0}
            sub={
              data && data.artifacts.total > 0
                ? `${data.artifacts.delivered} delivered`
                : "None built"
            }
            tone="neutral"
            onClick={() => setLocation("/artifacts")}
            icon={
              <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
                <rect
                  x="3"
                  y="4"
                  width="14"
                  height="3"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M4 7v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M8 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            }
          />
          <PipelineStage
            label="Connectors"
            value={data?.connectorsRegistered ?? 0}
            sub="Plug-ins registered"
            tone="brand"
            onClick={() => setLocation("/connectors")}
            icon={
              <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
                <path
                  d="M7 3v3m6-3v3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M5 6h10v3a5 5 0 0 1-10 0V6Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path d="M10 14v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            }
          />
        </div>
      </section>

      {/* == MAIN CONTENT == */}
      <section className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* LEFT — connectors in use / first-run setup */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between border-b border-rule px-5 py-4">
            <h2 className="text-[15px] font-semibold text-ink">
              {isNewSetup ? "Get started" : "Active connectors"}
            </h2>
            {!isNewSetup && (
              <button
                onClick={() => setLocation("/connectors")}
                className="text-[12px] font-medium text-brand-700 hover:underline"
              >
                All connectors →
              </button>
            )}
          </div>

          {isNewSetup ? (
            <SetupChecklist
              hasPlantId={!!data?.plantId}
              hasConnectors={(data?.connectorsRegistered ?? 0) > 0}
              hasSources={(data?.sources.total ?? 0) > 0}
              onNavigate={setLocation}
            />
          ) : (
            <ConnectorsList
              entries={[...sourceCountByConnector.entries()]}
              connectorById={connectorById}
              onNavigate={setLocation}
            />
          )}
        </div>

        {/* RIGHT — agent info + quick links */}
        <div className="flex flex-col gap-5">
          <AgentPanel data={data} isRunning={isRunning} onNavigate={setLocation} />
          <QuickLinks onNavigate={setLocation} />
        </div>
      </section>
    </div>
  );
}
